import React from 'react';
import { useTranslation, TFunction } from "react-i18next";
import styles from './OrientationGraph.module.scss';
import { CompanyPage, CompanyPageBid } from '../../../../../../hooks/slices/companySlice'; // Adjusted
import { DateUtil } from '../../../../../../utils/date'; // Adjusted
import {
    calculateMetricByYear,
    calculateYoYChange,
    getSimpleYoYSubtitleProps,
    reduceBidsByYear,
    SimpleYoYSubtitleResult // Ensure exported
} from '../../../info/infoUtils'; // Adjusted relative path
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    Line,
    Bar,
    Tooltip as RechartsTooltip,
    CartesianGrid,
    ReferenceLine,
    TooltipProps
} from 'recharts';
import { GraphTooltip } from '../../../../../../components/graphs/shared/GraphTooltip'; // Adjusted

interface OrientationGraphProps {
    companyPage: CompanyPage;
}

export const OrientationGraph: React.FC<OrientationGraphProps> = (props) => {
    const { t } = useTranslation();
    const companyPage: CompanyPage = props.companyPage;
    const bids: CompanyPageBid[] = companyPage.bids ?? [];
    const winningBids: CompanyPageBid[] = bids.filter(bid => bid.bidRole === "WINNER");

    // --- YoY Change Calculation --- 
    const currentYear = DateUtil.today().year();
    const lastYear = currentYear - 1;
    const relevantYears = [lastYear, currentYear]; // Only need these for YoY calc

    const yearlyWinningBids = reduceBidsByYear(winningBids); 

    // Calculate yearly price/quality counts
    type PriceQualityMetric = { priceCount: number; qualityCount: number };
    const yearlyPriceQualityCounts = calculateMetricByYear<PriceQualityMetric>(
        yearlyWinningBids, 
        relevantYears, 
        (bidsInYear: CompanyPageBid[]) => {
            const priceCount = bidsInYear.filter(bid => bid.awardCriteriumPricePercentage === 100).length;
            const qualityCount = bidsInYear.length - priceCount; 
            return { priceCount, qualityCount };
        },
        { priceCount: 0, qualityCount: 0 } // Default counts
    );

    // Helper to calculate price percentage from counts
    const calculatePricePercentage = (metric: PriceQualityMetric): number => {
        const totalWins = metric.priceCount + metric.qualityCount;
        return totalWins > 0 ? (metric.priceCount / totalWins) * 100 : 0;
    };

    const currentYearPercentage = calculatePricePercentage(yearlyPriceQualityCounts[currentYear.toString()]);
    const lastYearPercentage = calculatePricePercentage(yearlyPriceQualityCounts[lastYear.toString()]);
    
    // Use new YoY helper for percentage points difference
    const yearOverYearChange = calculateYoYChange(currentYearPercentage, lastYearPercentage, 'percentagePoints');

    // Determine if data is sufficient (both years must have > 0 total wins for a meaningful comparison)
    const currentYearTotalWins = yearlyPriceQualityCounts[currentYear.toString()].priceCount + yearlyPriceQualityCounts[currentYear.toString()].qualityCount;
    const lastYearTotalWins = yearlyPriceQualityCounts[lastYear.toString()].priceCount + yearlyPriceQualityCounts[lastYear.toString()].qualityCount;
    const hasSufficientData = yearOverYearChange !== null && currentYearTotalWins > 0 && lastYearTotalWins > 0;

    const changeText = hasSufficientData ? Math.abs(yearOverYearChange!).toFixed(1) : '';

    const subtitleProps: SimpleYoYSubtitleResult | null = hasSufficientData ? getSimpleYoYSubtitleProps({
        yearOverYearChange: yearOverYearChange!, 
        t: t,
        increaseKey: "companyPage.orientation_increase_price", 
        decreaseKey: "companyPage.orientation_decrease_price",
        noChangeKey: "companyPage.orientation_no_change", 
    }) : null;
    
    const overallOrientationPercentage = companyPage.percentageOfLotsThatOnlyDependOnPrice;

    // --- Data for Details Graph (requires calculation across multiple years) ---
    const detailYears = Array.from({length: 5}, (_, i) => DateUtil.today().subtract(4 - i, 'years').year());
    const yearlyPriceQualityCountsDetails = calculateMetricByYear<PriceQualityMetric>(
        yearlyWinningBids, // Use already grouped bids
        detailYears,       // Use detail years
        (bidsInYear: CompanyPageBid[]) => {   // Same calculation logic
            const priceCount = bidsInYear.filter(bid => bid.awardCriteriumPricePercentage === 100).length;
            const qualityCount = bidsInYear.length - priceCount; 
            return { priceCount, qualityCount };
        },
        { priceCount: 0, qualityCount: 0 } 
    );

    // Prepare data specifically for OrientationDetailsGraph using the detailed yearly counts
    const chartDataForDetails = detailYears.map(year => {
        const yearStr = year.toString();
        const counts = yearlyPriceQualityCountsDetails[yearStr];
        const percentage = calculatePricePercentage(counts);
        return {
            year: yearStr,
            percentage: percentage,
            priceCount: counts.priceCount,
            qualityCount: counts.qualityCount,
        };
    });

    // Calculate 5-year percentage based on total counts
    const fiveYearTotalPriceCount = chartDataForDetails.reduce((sum, data) => sum + data.priceCount, 0);
    const fiveYearTotalQualityCount = chartDataForDetails.reduce((sum, data) => sum + data.qualityCount, 0);
    const fiveYearTotalWins = fiveYearTotalPriceCount + fiveYearTotalQualityCount;
    const fiveYearCountBasedPercentage = fiveYearTotalWins > 0 
        ? (fiveYearTotalPriceCount / fiveYearTotalWins) * 100 
        : null;

    const displayPercentage = fiveYearCountBasedPercentage !== null ? fiveYearCountBasedPercentage : (overallOrientationPercentage ?? null);

    return (<div className={`
            ${styles["graph-card"]}
            ${styles["orientation-card"]}
        `}
    >
        <div className={styles["heading-container"]}>
            <span className={styles["graph-type-title"]}>{t("companyPage.orientation_graph_type", "Orientation")}</span>
            <h3 className={styles["last-year-percentage"]}>{`${displayPercentage?.toFixed(0) ?? 'N/A'}%`}</h3>
            {/* {hasSufficientData && subtitleProps ? (
                 <div className={`${styles.subtitle}`}> 
                     <span className={`${styles["change-indicator"]} ${styles[`change-indicator-${subtitleProps.colorSuffix}`]}`}> 
                         {`${subtitleProps.trendIcon}${changeText}% ${subtitleProps.changeDescription}`} 
                     </span> 
                 </div> 
             ) : (
            <div className={styles.subtitle}>
                {t("companyPage.orientation_title_2", "Price vs Quality Focus Trend")}
            </div>
             )} */}
        </div>
        <div className={styles["graph-container"]}>
            {/* Pass calculated data for the details graph */}
            <OrientationDetailsGraph data={chartDataForDetails} t={t} /> 
        </div>
    </div>)
}

// --- OrientationDetailsGraph --- 
interface OrientationDetailsGraphDataItem {
    year: string;
    percentage: number;
    priceCount: number;
    qualityCount: number;
}

interface OrientationDetailsGraphProps {
    data: OrientationDetailsGraphDataItem[];
    t: TFunction; // Pass t function for tooltip translations
}

const OrientationDetailsGraph: React.FC<OrientationDetailsGraphProps> = ({ data, t }) => {
    // const { t } = useTranslation(); // Already passed as prop

    // Domain calculations
    const percentageRange = 100;
    const percentageVisibleRatio = 0.5;
    const percentageTotalDomainHeight = percentageRange / percentageVisibleRatio;
    const percentagePadding = (percentageTotalDomainHeight - percentageRange) / 2;
    const yDomainPercent: [number, number] = [
        Math.round(0 - percentagePadding),
        Math.round(100 + percentagePadding),
    ];
    const maxAbsCount = Math.max(
        1, // Ensure domain is at least 1
        ...data.map(d => d.priceCount),
        ...data.map(d => Math.abs(d.qualityCount))
    );
    const countVisibleRatio = 0.25;
    const scaledMaxAbsCount = maxAbsCount / countVisibleRatio;
    const yDomainCountAbs = Math.ceil(scaledMaxAbsCount / 2);
    const yPiece = 3 * yDomainCountAbs / (2*3); // Height of one count unit relative to axis
    const yDomainCount: [number, number] = [
        0,
        2*yDomainCountAbs // Total height of the count axis
    ];
    // Need baseOffset for Bar positioning
    const parsedData = data.map(d => ({ 
        ...d,
        // Calculate the bottom position of the quality bar
        baseOffset: yPiece + 2*yPiece*d.percentage/100 - d.qualityCount, 
    }));

    const priceColor = "#AED581"; 
    const qualityColor = "#81C784"; 
    const percentageColor = "#546E7A";

    // --- Tooltip Refactoring --- 

    const orientationTitleExtractor = (payload: any[], label?: string) => {
        return label; // The year is passed as the label argument by Recharts
    };

    const orientationItemsExtractor = (payload: any[]) => {
        if (!payload || payload.length === 0) return [];
        // Access the specific payload item containing our data structure
        const itemPayload = payload[0].payload as OrientationDetailsGraphDataItem & { baseOffset: number }; 

        return [
            {
                label: t('companyPage.orientationTooltip.priceFocus', 'Price Focus'),
                value: itemPayload.percentage, 
                color: percentageColor, 
            },
            {
                label: t('companyPage.orientationTooltip.priceCount', 'Price Focused'),
                value: itemPayload.priceCount, 
                color: priceColor, 
            },
            {
                label: t('companyPage.orientationTooltip.qualityCount', 'Quality Focused'),
                value: itemPayload.qualityCount, 
                color: qualityColor, 
            },
        ];
    };

    const orientationValueFormatter = (value: any, label?: string) => {
        if (label === t('companyPage.orientationTooltip.priceFocus', 'Price Focus %')) {
            return `${Math.round(value as number)}%`;
        }
        // Assuming other values are counts
        return Math.round(value as number).toLocaleString(); 
    };

    const renderTooltip = (props: TooltipProps<number, string>) => {
        return (
            <GraphTooltip 
                active={props.active}
                payload={props.payload}
                titleExtractor={orientationTitleExtractor}
                itemsExtractor={orientationItemsExtractor}
                valueFormatter={orientationValueFormatter}
            />
        );
    };
    // --- End Tooltip Refactoring --- 

    return (
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
                data={parsedData} // Use parsedData which includes baseOffset
                margin={{ top: 5, right: 5, left: 10, bottom: 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                    dataKey="year"
                    axisLine={false}
                    tickLine={false}
                    padding={{ left: 10, right: 10 }}
                />
                <YAxis
                    yAxisId="left" // For Percentage Line
                    domain={yDomainPercent}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(tick) => `${tick}%`}
                    width={40}
                    ticks={[0, 50, 100]}
                />
                <YAxis
                    yAxisId="right" // For Count Bars
                    orientation="right"
                    domain={yDomainCount}
                    axisLine={false}
                    tickLine={false}
                    tick={false} // Hide ticks on this axis
                    width={1}
                />

                {/* Tooltip - Use the custom render function */}
                <RechartsTooltip 
                    content={renderTooltip} 
                    cursor={{ fill: 'transparent' }} // Optional: style cursor
                    position={{ x: 0, y: -40 }} // Adjust position as needed
                    offset={10} 
                    isAnimationActive={false}
                    allowEscapeViewBox={{ x: true, y: true }} // Allow tooltip to overflow slightly
                />

                {/* Reference line at 0 for the count axis */}
                <ReferenceLine yAxisId="right" y={0} stroke="var(--border-color-light)" strokeDasharray="3 3" />

                {/* Bars for counts */} 
                {/* Transparent base bar to position the stack correctly */}
                <Bar yAxisId="right" dataKey="baseOffset" stackId="counts" fill="transparent" barSize={20} />
                {/* Quality counts bar (negative values will render downwards from baseOffset) */}
                <Bar yAxisId="right" dataKey="qualityCount" stackId="counts" fill={qualityColor} radius={[1, 1, 3, 3]} />
                {/* Price counts bar (positive values render upwards from the top of quality bar) */}
                <Bar yAxisId="right" dataKey="priceCount" stackId="counts" fill={priceColor} radius={[3, 3, 1, 1]} />
                
                {/* Line for percentage */}
                <Line yAxisId="left" type="monotone" dataKey="percentage" stroke={percentageColor} strokeWidth={2} activeDot={{ r: 6, fill: percentageColor }} dot={false} />
            </ComposedChart>
        </ResponsiveContainer>
    );
} 